import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { NamedLink } from '../../components';
import { hideTidioChat, showTidioChat } from '../../util/tidioChat';
import css from './ListingPage.module.css';

const normalizeLineBreaks = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\r\n|\r/g, '\n').trim();
};

const SellerInfoPanel = ({ isOpen, onClose, author }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    hideTidioChat();
    document.body.style.overflow = 'hidden';
    const handleEscape = e => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      showTidioChat();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const profile = author?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const displayName = publicData.displayName || profile.displayName || 'Vendeur';
  const siret = publicData.siren;
  const address = normalizeLineBreaks(publicData.adresse);
  const bio = normalizeLineBreaks(profile.bio);
  const cgvLink = publicData.lien_cgv;
  const profileId = author?.id?.uuid;

  return ReactDOM.createPortal(
    <>
      <div className={css.sellerPanelOverlay} onClick={onClose} />
      <aside className={css.sellerPanel} aria-modal="true" role="dialog" aria-label="Informations vendeur">
        <header className={css.sellerPanelHeader}>
          <h2 className={css.sellerPanelTitle}>Informations vendeur</h2>
          <button type="button" className={css.sellerPanelClose} onClick={onClose} aria-label="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </header>

        <div className={css.sellerPanelContent}>
          <section className={css.sellerPanelIdentity}>
            <h3 className={css.sellerPanelName}>{displayName}</h3>

            {siret ? (
              <p className={css.sellerPanelField}>
                <span className={css.sellerPanelFieldLabel}>SIRET:</span> {siret}
              </p>
            ) : null}

            {address ? (
              <p className={css.sellerPanelFieldPreLine}>
                <span className={css.sellerPanelFieldLabel}>Adresse:</span> {address}
              </p>
            ) : null}
          </section>

          {bio ? (
            <section className={css.sellerPanelSection}>
              <h4 className={css.sellerPanelSectionTitle}>Bio</h4>
              <p className={css.sellerPanelParagraph}>{bio}</p>
            </section>
          ) : null}

          {cgvLink ? (
            <section className={css.sellerPanelSection}>
              <h4 className={css.sellerPanelSectionTitle}>Conditions générales de vente</h4>
              <a
                href={cgvLink}
                target="_blank"
                rel="noopener noreferrer"
                className={css.sellerPanelExternalLink}
              >
                Consulter les CGV du vendeur
              </a>
            </section>
          ) : null}

          {profileId ? (
            <NamedLink
              name="ProfilePage"
              params={{ id: profileId }}
              className={css.sellerPanelProfileButton}
              onClick={onClose}
            >
              Voir la fiche vendeur et les CGV
            </NamedLink>
          ) : null}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default SellerInfoPanel;
